import { ThemeProvider } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getLastPosition } from '../../../chess/gameTree'
import AppLayout from '../../../components/AppLayout/AppLayout'
import { PuzzleStreakOverlay, StreakAnimation } from '../../../components/PuzzleStreakOverlay/PuzzleStreakOverlay'
import { Dialogs, useDialogs } from '../../../sharedComponents/src/Contexts/DialogsContext'
import { LoginState } from '../../../sharedComponents/src/globalHeader/GlobalHeader'
import { useScreenSize } from '../../../sharedComponents/src/hooks/useScreenSize'
import theme from '../../../sharedComponents/src/Theme/theme-v2'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import PuzzlesChessBoard from '../../chessboard/PuzzlesChessboard'
import HintLimitDialog from '../../PuzzlesView/components/HintLimitDialog'
import PuzzlesLimitDialog from '../../PuzzlesView/components/v2/PuzzleLimitDialog'
import useResultSounds from '../../PuzzlesView/hooks/useResultSounds'
import DailyPuzzleCharacter from './components/DailyPuzzleCharacter'
import DailyPuzzleFeedback from './components/DailyPuzzleFeedback'
import DailyPuzzleStreak from './components/DailyPuzzleStreak'
import DailyPuzzleTitle from './components/DailyPuzzleTitle'
import DesktopDP from './layouts/DesktopDP'
import MobileDP from './layouts/MobileDP'

const dailyPuzzleViewV2: React.FC = () => {
    const { date } = useParams<{ date?: string }>()
    const userState = useStoreState((state) => state.userData.state)
    const userData = useStoreState((state) => state.userData.userData)
    const settings = useStoreState((state) => state.gameView.settings)

    const gameTree = useStoreState((state) => state.dailyPuzzleViewV2.gameTree)
    const currentPosition = useStoreState((state) => state.dailyPuzzleViewV2.currentPosition)
    const flipped = useStoreState((state) => state.dailyPuzzleViewV2.flipped)
    const myColor = useStoreState((state) => state.dailyPuzzleViewV2.myColor)
    const hint = useStoreState((state) => state.dailyPuzzleViewV2.hint)
    const customArrows = useStoreState((state) => state.dailyPuzzleViewV2.customArrows)
    const puzzleState = useStoreState((state) => state.dailyPuzzleViewV2.puzzleState)
    const showSolution = useStoreState((state) => state.dailyPuzzleViewV2.showSolution)
    const joinUsOpen = useStoreState((state) => state.dailyPuzzleViewV2.joinUsOpen)
    const showHintLimit = useStoreState((state) => state.dailyPuzzleViewV2.showHintLimit)
    const streak = useStoreState((state) => state.dailyPuzzleViewV2.streak)
    const showStreakAnimation = useStoreState((state) => state.dailyPuzzleViewV2.showStreakAnimation)
    const showFeedbackDialog = useStoreState((state) => state.dailyPuzzleViewV2.showFeedbackDialog)
    const puzzleId = useStoreState((state) => state.dailyPuzzleViewV2.puzzleId)
    const feedbackMessage = useStoreState((state) => state.dailyPuzzleViewV2.feedbackMessage)
    const showStreak = useStoreState((state) => state.dailyPuzzleViewV2.showStreak)
    const performanceGroupID = useStoreState((state) => state.dailyPuzzleViewV2.performanceGroupID)

    const handleMove = useStoreActions((actions) => actions.dailyPuzzleViewV2.handleMove)
    const getPuzzle = useStoreActions((actions) => actions.dailyPuzzleViewV2.getPuzzle)
    const setJoinUsOpen = useStoreActions((actions) => actions.dailyPuzzleViewV2.setJoinUsOpen)
    const setShowStreakAnimation = useStoreActions((actions) => actions.dailyPuzzleViewV2.setShowStreakAnimation)
    const setShowHintLimit = useStoreActions((actions) => actions.dailyPuzzleViewV2.setShowHintLimit)
    const resetAllStates = useStoreActions((actions) => actions.dailyPuzzleViewV2.resetAllStates)

    const navigate = useNavigate()
    const { isMobile } = useScreenSize()
    const playResultSound = useResultSounds(settings)
    const { registerDialog, unregisterDialog } = useDialogs()
    const disableByMoveList = useMemo(
        () => currentPosition.id !== getLastPosition(gameTree).id,
        [currentPosition.id, gameTree],
    )

    // HIDE HINT LIMIT MESSAGE
    const handleClickAnywhere = () => {
        setShowHintLimit(false)
    }
    useEffect(() => {
        document.addEventListener('mousedown', handleClickAnywhere)
        return () => {
            document.removeEventListener('mousedown', handleClickAnywhere)
            resetAllStates()
            unregisterDialog(Dialogs.PUZZLE_LIMIT)
            unregisterDialog(Dialogs.DAILY_PUZZLE_FEEDBACK)
            unregisterDialog(Dialogs.HINT_LIMIT)
        }
    }, [])

    // REGISTER PUZZLE LIMIT DIALOG
    useEffect(() => {
        if (joinUsOpen) {
            registerDialog(
                Dialogs.PUZZLE_LIMIT,
                <PuzzlesLimitDialog variant="puzzleDailyMember" onClose={() => setJoinUsOpen(false)} />,
            )
        } else {
            unregisterDialog(Dialogs.PUZZLE_LIMIT)
        }
    }, [joinUsOpen])

    // REGISTER FEEDBACK DIALOG
    useEffect(() => {
        if (showFeedbackDialog) {
            registerDialog(
                Dialogs.DAILY_PUZZLE_FEEDBACK,
                <DailyPuzzleFeedback
                    puzzleId={puzzleId}
                    streak={streak.currentStreak}
                    feedbackMessage={feedbackMessage}
                    showStreak={showStreak}
                    performanceGroupID={performanceGroupID}
                />,
            )
        }
    }, [showFeedbackDialog])

    // REGISTER HINT LIMIT DIALOG
    useEffect(() => {
        if (showHintLimit) {
            registerDialog(Dialogs.HINT_LIMIT, <HintLimitDialog />)
        }
    }, [showHintLimit])

    // LOAD PUZZLE DATA
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (userState === LoginState.LOGGED_IN) {
                setJoinUsOpen(false)
                resetAllStates()
                getPuzzle({ navigate, date })
            } else {
                setJoinUsOpen(true)
                resetAllStates()
            }
        }, 1)
        return () => {
            clearTimeout(timeout)
        }
    }, [userState, userData?.user_name, date, getPuzzle, setJoinUsOpen, resetAllStates])

    // RESULT SOUND
    useEffect(() => {
        if (puzzleState !== null) {
            puzzleState ? playResultSound(true) : playResultSound(false)
        }
    }, [puzzleState])

    const handleAnimationEnd = () => {
        setShowStreakAnimation(false)
    }

    return (
        <AppLayout
            compactChild
            aboveBoard={
                !isMobile ? undefined : (
                    <ThemeProvider theme={theme}>
                        {/* Title */}
                        <DailyPuzzleTitle />
                        {/* Streak */}
                        <DailyPuzzleStreak />
                        {/* Character */}
                        <DailyPuzzleCharacter />
                    </ThemeProvider>
                )
            }
            board={
                <>
                    {showStreakAnimation && (
                        <PuzzleStreakOverlay
                            value={streak.currentStreak}
                            variant={StreakAnimation.DAILY}
                            onAnimationEnd={handleAnimationEnd}
                        />
                    )}
                    <PuzzlesChessBoard
                        mode="puzzle"
                        gameTree={gameTree}
                        currentPositionId={currentPosition.id}
                        flipped={flipped}
                        onMove={handleMove}
                        myColor={myColor}
                        hint={hint}
                        customArrows={customArrows}
                        disableBoard={currentPosition.position.turn !== myColor || showSolution || disableByMoveList}
                        moveStatus={puzzleState === null ? undefined : puzzleState}
                    />
                </>
            }
        >
            <ThemeProvider theme={theme}>{isMobile ? <MobileDP /> : <DesktopDP />}</ThemeProvider>
        </AppLayout>
    )
}

export default dailyPuzzleViewV2
